import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import type {
  MatchFieldsFragment as MatchData,
  PendingMatchFieldsFragment as PendingMatchData
} from 'src/graphql/types'
import { PendingMatchStatus } from 'src/graphql/types'
import { MatchLifecycle } from 'src/screens/matches/collection/MatchLifecycle'
import { hasTimedOut } from 'src/screens/matches/collection/match/status/utils'

export function MatchesList(props: {
  matches: (PendingMatchData | MatchData)[]
}) {
  const { t } = useTranslation()
  const allMatches = props.matches ?? []

  const validMatches: (PendingMatchData | MatchData)[] = []
  let errorCount = 0

  for (const match of allMatches) {
    if (isErroredMatch(match)) {
      errorCount++
    } else {
      validMatches.push(match)
    }
  }

  const errorKey =
    errorCount === 1 ? 'matches:hidden_errors' : 'matches:hidden_errors_plural'

  return (
    <>
      {errorCount > 0 && (
        <ErrorSummary>{t(errorKey, { count: errorCount })}</ErrorSummary>
      )}
      <Box>
        {validMatches.map((match) => (
          <MatchLifecycle key={match.id} source={match} />
        ))}
      </Box>
    </>
  )
}

function isErroredMatch(match: PendingMatchData | MatchData): boolean {
  if (!('status' in match)) return false

  const { status, updatedAt, courseProgress } = match
  const timedOut = hasTimedOut(updatedAt)

  const isDone = status === PendingMatchStatus.Completed && courseProgress === 1
  const isDoneViaTimeout = status === PendingMatchStatus.Completed && timedOut
  const isFailed = status === PendingMatchStatus.Failed
  const isFinished = isDone || isDoneViaTimeout || isFailed

  if (isFinished || !timedOut) return false

  return (
    status === PendingMatchStatus.Created ||
    status === PendingMatchStatus.Verified
  )
}

const Box = styled.div`
  display: flex;
  flex-direction: column;
`

const ErrorSummary = styled.div`
  margin-top: ${(props) => props.theme.d.spacing[3]};
  font-size: 0.875rem;
  color: ${(props) => props.theme.d.colors.text.primary};
`
